import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hideBackButton } from '@telegram-apps/sdk-react';
import { TailSpin } from 'react-loader-spinner';
import { BottomNav } from '../components/design/BottomNav';
import { InlineCTA } from '../components/design/InlineCTA';
import { brand, fontFamily, tg } from '../theme/tokens';
import { getGeneration, type Generation } from '../lib/api';
import { describeGenerationError } from '../lib/generationErrors';
import { useWizard } from '../state/wizard';

const POLL_INTERVAL_MS = 4000;

/**
 * Step 5 — generation in progress.
 *
 * Mockup: 2-column "spinner-track" up top (image / video), hero animation
 * in middle that shifts per phase (orbital bubbles → pair-thumb → success
 * heart → failed heart), status copy below, and either a passive close-
 * hint or a CTA depending on terminal state.
 *
 * Phase derivation from API state (no per-phase status column yet):
 *   - image phase running: status='processing' AND pair_image_key is null
 *   - image done, video running: status='processing' AND pair_image_key set
 *   - both done: status='done' (auto-redirect to /result)
 *   - failed: status='failed' (terminal — show error + Try again)
 *
 * Retry awareness: image_attempts/video_attempts >= 2 → "Trying again…"
 * banner + status copy switch.
 *
 * We keep the polling-based approach (every 4s). A future enhancement is
 * switching to Supabase Realtime subscriptions — needs anon-key + RLS
 * wiring, parked as a separate task.
 */
export function GeneratingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = (location.state as { id?: string } | null)?.id;

  const [gen, setGen] = useState<Generation | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Photos from wizard state are passed into the orbital-bubble animation
  // so users see THEIR actual photos rotating, not "You"/"Mira" placeholders.
  // Available because we no longer reset() on payment success (so they
  // survive the navigation Pay → /generating).
  const { photo1, photo2, reset } = useWizard();

  // Once we land on /generating, the user has paid and the pipeline is
  // running server-side — wizard state has been "consumed". Reset it as
  // soon as we leave this page (to /result, /, /library, /profile, …),
  // so a fresh visit to / starts with empty photo slots instead of the
  // already-spent ones.
  //
  // Ref-capture pattern keeps the cleanup stable: empty deps array means
  // cleanup fires exactly once on unmount in prod (and once during the
  // dev Strict-Mode double-invoke, which is harmless — the wipe just
  // means orbital bubbles momentarily fall back to gradients before the
  // re-mount; not visible to real users).
  const resetRef = useRef(reset);
  resetRef.current = reset;
  useEffect(() => {
    return () => resetRef.current();
  }, []);

  // Hide BackButton — once paid, the generation can't be cancelled
  // mid-flight. We don't manage MainButton (replaced w/ InlineCTA).
  useEffect(() => {
    try { hideBackButton(); } catch { /* */ }
  }, []);

  useEffect(() => {
    if (!id) navigate('/', { replace: true });
  }, [id, navigate]);

  // Poll generation. On 'done' redirect to /result. On 'failed' stay on
  // page to show the failure UI (refund banner + Try again CTA).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const next = await getGeneration(id);
        if (cancelled) return;
        setGen(next);

        if (next.status === 'done' && next.output_video_url) {
          // Short delay so user sees the success state briefly before
          // navigating. Better feedback than an abrupt jump.
          setTimeout(() => {
            if (!cancelled) {
              navigate('/result', {
                replace: true,
                state: {
                  videoUrl: next.output_video_url,
                  kissType: next.kiss_type,
                  durationSec: next.duration_sec,
                  scene: next.scene,
                },
              });
            }
          }, 1200);
          return;
        }
        if (next.status === 'failed') return; // terminal — no more polling
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        console.error('poll failed', e);
        setError(e instanceof Error ? e.message : 'Network error');
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (!id) return null;

  // Derive 2-phase state from `gen`. Until first poll lands we render an
  // optimistic "image phase active" view (matches what happens on the
  // server: post-payment, the pipeline starts the image step immediately).
  const phase = derivePhase(gen);

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily,
        paddingBottom: 100,
      }}
    >
      <SpinnerTrack phase={phase} />

      {/* Retry banner — shown while a retry is in flight. After the retry
          step completes (or fails terminally), it falls away naturally. */}
      {phase.retrying && (
        <InfoBanner
          tone="brand"
          title="First attempt didn't go through"
          hint="Happens sometimes. We've started a second attempt automatically — should be ready in a couple more minutes."
          icon="↻"
        />
      )}

      {/* Failure refund banner */}
      {phase.kind === 'failed' && (
        <InfoBanner
          tone="error"
          title="⭐ Your stars are on their way back"
          hint="We've refunded your payment automatically — should appear in your Telegram balance shortly."
          icon="!"
        />
      )}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          // Top padding intentionally generous (~80px instead of 20). When
          // the user lands here straight from a successful Telegram Stars
          // purchase, the Telegram WebApp client overlays a native receipt
          // toast ("You acquired 'X' for N Star.") that sits in the upper
          // ~60-70px of the viewport for ~2-3s. Without this padding the
          // toast covers the orbital photo bubbles mid-animation — looks
          // like a UI bug. With it, the toast lands in the empty band
          // between SpinnerTrack and Hero, leaving the bubbles visible.
          //
          // Trade-off: when entering /generating from Library (no toast,
          // just navigating to view a processing kiss), this padding is
          // "wasted" — but it just reads as extra breathing room between
          // the phase indicator and the hero, not broken. Acceptable.
          //
          // Can't suppress the toast: Telegram's SDK has no
          // silentPayment / suppressConfirmation option — the receipt is
          // mandatory platform UX shown for every Stars transaction.
          padding: '80px 24px 28px',
          textAlign: 'center',
        }}
      >
        <Hero
          phase={phase}
          pairImageUrl={gen?.pair_image_url ?? null}
          photo1={photo1}
          photo2={photo2}
        />

        <h2
          style={{
            margin: '12px 0 6px',
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: -0.4,
            color: phase.kind === 'failed' ? tg.text : tg.text,
          }}
        >
          {phase.title}
        </h2>
        <div style={{ fontSize: 14, color: tg.textHint, lineHeight: 1.4, maxWidth: 320 }}>
          {phase.subtitle}
        </div>

        {/* Soft network error nudge — non-blocking, retries are automatic */}
        {error && phase.kind !== 'failed' && (
          <div style={{ marginTop: 12, fontSize: 11, color: tg.textHint }}>
            (reconnecting…)
          </div>
        )}

        {/* Terminal-state CTAs */}
        {phase.kind === 'success' && (
          <div style={{ width: '100%', marginTop: 24 }}>
            <InlineCTA
              onClick={() => {
                if (gen?.output_video_url) {
                  navigate('/result', {
                    replace: true,
                    state: {
                      videoUrl: gen.output_video_url,
                      kissType: gen.kiss_type,
                      durationSec: gen.duration_sec,
                      scene: gen.scene,
                    },
                  });
                }
              }}
            >
              View your kiss
            </InlineCTA>
          </div>
        )}

        {phase.kind === 'failed' && (
          <div style={{ width: '100%', marginTop: 24 }}>
            <InlineCTA onClick={() => navigate('/', { replace: true })}>
              Try again with different photos
            </InlineCTA>
          </div>
        )}
      </div>

      {/* Bottom hint — only for in-progress phases. Replaced by the
          terminal CTA(s) once we know the outcome. */}
      {(phase.kind === 'image' || phase.kind === 'video') && (
        <div
          style={{
            margin: '0 20px 8px',
            padding: 10,
            textAlign: 'center',
            fontSize: 12,
            color: tg.textHint,
            background: tg.bg2,
            borderRadius: 12,
          }}
        >
          💌 Feel free to close — we'll notify you when ready
        </div>
      )}

      <BottomNav />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Phase derivation
// ────────────────────────────────────────────────────────────────────
interface Phase {
  /** Coarse phase. Drives spinner-track state + hero animation choice. */
  kind: 'image' | 'video' | 'success' | 'failed';
  /** True when current step's attempts > 1 → "retrying" UI variant. */
  retrying: boolean;
  /** Per-row state for the spinner-track (image row, then video row). */
  rows: [SpinnerRowState, SpinnerRowState];
  /** Status hero title — large bold copy below the hero animation. */
  title: string;
  /** Status hero subtitle — single line of muted body copy. */
  subtitle: string;
}

type SpinnerRowState =
  | { state: 'muted'; label: string }
  | { state: 'active'; label: string; attempt?: number }
  | { state: 'done'; label: string }
  | { state: 'failed'; label: string };

function derivePhase(gen: Generation | null): Phase {
  // Pre-first-poll optimistic view: assume image phase is starting.
  if (!gen) {
    return {
      kind: 'image',
      retrying: false,
      rows: [
        { state: 'active', label: 'Generating image' },
        { state: 'muted', label: 'Animating video' },
      ],
      title: 'Creating your kiss…',
      subtitle: 'Finding the perfect angle for the moment',
    };
  }

  const imageAttempts = gen.image_attempts ?? 0;
  const videoAttempts = gen.video_attempts ?? 0;

  if (gen.status === 'done') {
    return {
      kind: 'success',
      retrying: false,
      rows: [
        { state: 'done', label: 'Image ready' },
        { state: 'done', label: 'Video ready' },
      ],
      title: "It's ready! 💋",
      subtitle: 'Your kiss video is waiting for you',
    };
  }

  if (gen.status === 'failed') {
    // We don't strictly know which step failed without more API fields;
    // use heuristic: if pair_image_key is null, image failed. Otherwise
    // video failed. Either way the user-facing fix is the same: retry
    // with different photos (image step is the photo-sensitive one).
    const imageFailed = !gen.pair_image_key;
    // IMPORTANT: never render gen.error_message verbatim — those strings
    // are raw provider traces ("key_revoked — Unauthorized – Authentication
    // failed. Please check that your Authorization and Content-Type headers
    // are correctly set."). They scare users AND leak vendor / auth details.
    // Categorize into friendly copy. The raw message stays server-side for
    // support / audit.
    const display = describeGenerationError(gen.error_message);
    return {
      kind: 'failed',
      retrying: false,
      rows: [
        imageFailed
          ? { state: 'failed', label: 'Image generation failed' }
          : { state: 'done', label: 'Image ready' },
        imageFailed
          ? { state: 'muted', label: 'Animating video' }
          : { state: 'failed', label: 'Video generation failed' },
      ],
      title: display.title,
      subtitle: display.hint,
    };
  }

  // status === 'pending' | 'processing'
  if (!gen.pair_image_key) {
    // Image phase in progress
    const retrying = imageAttempts >= 2;
    return {
      kind: 'image',
      retrying,
      rows: [
        {
          state: 'active',
          label: 'Generating image',
          attempt: imageAttempts > 1 ? imageAttempts : undefined,
        },
        { state: 'muted', label: 'Animating video' },
      ],
      title: retrying ? 'Trying again…' : 'Creating your kiss…',
      subtitle: retrying
        ? 'Sorry for the wait, hang on'
        : 'Finding the perfect angle for the moment',
    };
  }

  // Image done, video in progress
  const retrying = videoAttempts >= 2;
  return {
    kind: 'video',
    retrying,
    rows: [
      { state: 'done', label: 'Image ready' },
      {
        state: 'active',
        label: 'Animating video',
        attempt: videoAttempts > 1 ? videoAttempts : undefined,
      },
    ],
    title: retrying ? 'Trying again…' : 'Pair preview is ready! ✨',
    subtitle: retrying
      ? 'Sorry for the wait, hang on'
      : 'Animating the kiss — adding cinematic motion',
  };
}

// ────────────────────────────────────────────────────────────────────
// Spinner track — 2 columns at top of page
// ────────────────────────────────────────────────────────────────────
function SpinnerTrack({ phase }: { phase: Phase }) {
  return (
    <div style={{ padding: '24px 20px 4px', display: 'flex', gap: 12 }}>
      <SpinnerRow row={phase.rows[0]} />
      <SpinnerRow row={phase.rows[1]} />
    </div>
  );
}

function SpinnerRow({ row }: { row: SpinnerRowState }) {
  let labelColor: string;
  let labelWeight: number;
  switch (row.state) {
    case 'muted':   labelColor = tg.textHint; labelWeight = 500; break;
    case 'active':  labelColor = brand.solid; labelWeight = 600; break;
    case 'done':    labelColor = tg.text;     labelWeight = 600; break;
    case 'failed':  labelColor = tg.destructive; labelWeight = 600; break;
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <div style={{ width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {row.state === 'active' && (
          <TailSpin
            height={52}
            width={52}
            color={brand.solid}
            strokeWidth={4}
            ariaLabel="In progress"
          />
        )}
        {row.state === 'muted' && (
          // Muted (waiting) state — static dim ring, no animation. Keeps
          // the slot occupied so the row label stays vertically aligned
          // with the active row's spinner.
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 999,
              border: `4px solid ${tg.divider}`,
              opacity: 0.5,
            }}
          />
        )}
        {row.state === 'done' && (
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 999,
              background: brand.solid,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 6px 16px ${brand.solid}66`,
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
        {row.state === 'failed' && (
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 999,
              background: tg.destructive,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(235, 85, 69, 0.3)',
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </div>
        )}
      </div>
      <div style={{ fontSize: 14, fontWeight: labelWeight, color: labelColor, lineHeight: 1.3 }}>
        {row.label}
        {row.state === 'active' && row.attempt && (
          <span style={{ display: 'block', opacity: 0.7, fontWeight: 500, fontSize: 12, marginTop: 2 }}>
            (attempt {row.attempt})
          </span>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Hero — animation that swaps per phase
// ────────────────────────────────────────────────────────────────────
function Hero({
  phase,
  pairImageUrl,
  photo1,
  photo2,
}: {
  phase: Phase;
  pairImageUrl: string | null;
  photo1: File | null;
  photo2: File | null;
}) {
  return (
    <div
      style={{
        width: 220,
        height: 220,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 28,
      }}
    >
      {phase.kind === 'image' && <OrbitalBubbles photo1={photo1} photo2={photo2} />}
      {phase.kind === 'video' && <PairThumbHero pairImageUrl={pairImageUrl} />}
      {phase.kind === 'success' && <SuccessHero />}
      {phase.kind === 'failed' && <FailedHero />}
    </div>
  );
}

function OrbitalBubbles({ photo1, photo2 }: { photo1: File | null; photo2: File | null }) {
  // Object URLs for the two photos with cleanup. We re-create on file
  // change (rare during the Generating page — wizard photos are frozen
  // post-payment) and revoke on unmount to avoid blob leaks.
  const url1 = useFileObjectUrl(photo1);
  const url2 = useFileObjectUrl(photo2);

  return (
    <>
      <PhotoBubble
        url={url1}
        size={100}
        position={{ left: 12, top: 40 }}
        fallbackGradient="linear-gradient(135deg, #ffb088 0%, #e5407a 100%)"
        orbit="left"
      />
      <PhotoBubble
        url={url2}
        size={90}
        position={{ right: 12, bottom: 30 }}
        fallbackGradient="linear-gradient(135deg, #c44569 0%, #7b2d8e 100%)"
        orbit="right"
      />
      <div
        style={{
          position: 'absolute',
          zIndex: 10,
          width: 44,
          height: 44,
          borderRadius: 999,
          background: brand.solid,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 8px 22px ${brand.solid}66`,
          animation: 'dk-pulse-heart 1.8s ease-in-out infinite',
        }}
      >
        <svg width={20} height={20} viewBox="0 0 24 24" fill="#fff">
          <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
        </svg>
      </div>
      <style>{`
        @keyframes dk-orbit-left {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, 10px); }
        }
        @keyframes dk-orbit-right {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-20px, -10px); }
        }
        @keyframes dk-pulse-heart {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </>
  );
}

/**
 * Orbital photo bubble. When `url` is provided, renders the user's photo
 * as a circular thumbnail. When null (e.g. user reopened the app mid-
 * generation and wizard photos are gone), falls back to the original
 * brand gradient — same visual silhouette, no awkward empty placeholder.
 */
function PhotoBubble({
  url,
  size,
  position,
  fallbackGradient,
  orbit,
}: {
  url: string | null;
  size: number;
  position: { left?: number; right?: number; top?: number; bottom?: number };
  fallbackGradient: string;
  orbit: 'left' | 'right';
}) {
  return (
    <div
      style={{
        position: 'absolute',
        ...position,
        width: size,
        height: size,
        borderRadius: 999,
        overflow: 'hidden',
        backgroundImage: url ? `url("${url}")` : fallbackGradient,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: `0 8px 24px ${brand.solid}66`,
        animation: `dk-orbit-${orbit} 3.6s ease-in-out infinite`,
      }}
    />
  );
}

/**
 * Manage an object URL for a File. Returns null when file is null, or
 * the URL string otherwise. Revokes on file change / unmount.
 */
function useFileObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function PairThumbHero({ pairImageUrl }: { pairImageUrl: string | null }) {
  // Pair-preview thumb with two concentric ripple rings to communicate
  // "next step in progress". When real pair image lands (pair_image_url
  // populated), it shows as the thumb background; otherwise a brand
  // gradient placeholder.
  return (
    <div
      style={{
        width: 180,
        height: 240,
        borderRadius: 22,
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: pairImageUrl
          ? `url("${pairImageUrl}")`
          : 'linear-gradient(180deg, #FFB088 0%, #E5407A 50%, #7B2D8E 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: `0 12px 32px ${brand.solid}55`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 22,
          border: `2px solid ${brand.solid}88`,
          animation: 'dk-ripple 1.8s ease-out infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 22,
          border: `2px solid ${brand.solid}88`,
          animation: 'dk-ripple 1.8s ease-out infinite',
          animationDelay: '0.6s',
        }}
      />
      <style>{`
        @keyframes dk-ripple {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.15); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function SuccessHero() {
  // Confetti at fixed positions matching the mockup. Static (no animation)
  // because the user only sees this state for ~1.2s before auto-nav.
  const confetti: { color: string; top?: number; left?: number; right?: number; bottom?: number; rotate: number }[] = [
    { color: '#ff6b9d', top: 20, left: 20, rotate: 20 },
    { color: '#f4b23e', top: 30, right: 40, rotate: -15 },
    { color: '#6169ee', bottom: 40, left: 30, rotate: 30 },
    { color: '#c44569', bottom: 20, right: 20, rotate: -25 },
    { color: '#8e64ff', top: 60, left: 60, rotate: 45 },
    { color: '#ff9a6c', bottom: 60, right: 60, rotate: -35 },
  ];
  return (
    <>
      {confetti.map((c, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 8,
            height: 8,
            borderRadius: 2,
            background: c.color,
            top: c.top,
            left: c.left,
            right: c.right,
            bottom: c.bottom,
            transform: `rotate(${c.rotate}deg)`,
          }}
        />
      ))}
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: 999,
          background: brand.solid,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 20px 50px ${brand.solid}77`,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <svg width={48} height={48} viewBox="0 0 24 24" fill="#fff">
          <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
        </svg>
      </div>
    </>
  );
}

function FailedHero() {
  return (
    <div
      style={{
        width: 120,
        height: 120,
        borderRadius: 999,
        background: 'rgba(235, 85, 69, 0.15)',
        border: `2px solid ${tg.destructive}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ color: tg.destructive }}>
        <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
        <line x1="9" y1="9" x2="15" y2="15" />
        <line x1="15" y1="9" x2="9" y2="15" />
      </svg>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Info banner (brand for retry, error for refund)
// ────────────────────────────────────────────────────────────────────
function InfoBanner({
  tone,
  title,
  hint,
  icon,
}: {
  tone: 'brand' | 'error';
  title: string;
  hint: string;
  icon: string;
}) {
  const bg = tone === 'brand' ? 'rgba(229, 64, 122, 0.1)' : 'rgba(235, 85, 69, 0.12)';
  const border = tone === 'brand' ? 'rgba(229, 64, 122, 0.3)' : 'rgba(235, 85, 69, 0.3)';
  const iconBg = tone === 'brand' ? brand.solid : tg.destructive;
  return (
    <div
      style={{
        margin: '12px 20px 0',
        padding: '12px 14px',
        borderRadius: 12,
        background: bg,
        border: `0.5px solid ${border}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: iconBg,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, fontSize: 13, color: tg.text, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div style={{ color: tg.textHint, fontSize: 12 }}>{hint}</div>
      </div>
    </div>
  );
}
